import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Loader2,
  TrendingUp,
  Car,
  Target,
  Calendar,
  BarChart3,
  ArrowUp,
  ArrowDown,
  Minus
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

export default function PerformancePage() {
  const [loading, setLoading] = useState(true);
  const [objectives, setObjectives] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [seasonalityData, setSeasonalityData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedConcession, setSelectedConcession] = useState("all");
  const [user, setUser] = useState(null);

  const monthNames = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
  const monthNamesFull = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  
  const quarterNames = ["T1", "T2", "T3", "T4"];
  const quarterMonths = {
    1: [1, 2, 3],
    2: [4, 5, 6],
    3: [7, 8, 9],
    4: [10, 11, 12]
  };

  const COLORS = ['#E30613', '#0F172A', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'];

  // Check if selected concession is Fleet
  // For managers, check their assigned concession; for admin with selection, check selected concession
  const selectedConcessionData = concessions.find(c => c.id === selectedConcession);
  const userConcessionData = user?.concession_id ? concessions.find(c => c.id === user.concession_id) : null;
  const isFleetSelected = 
    (selectedConcessionData?.is_fleet === true || selectedConcessionData?.objective_type === "quarterly") ||
    (selectedConcession === "all" && user?.role === "manager" && (userConcessionData?.is_fleet === true || userConcessionData?.objective_type === "quarterly"));

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedConcession]);

  const fetchData = async () => {
    try {
      const [userRes, objectivesRes, concessionsRes, seasonalityRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/objectives", { params: { year: parseInt(selectedYear) } }),
        api.get("/concessions"),
        api.get("/seasonality")
      ]);
      
      setUser(userRes.data);
      setObjectives(objectivesRes.data);
      setConcessions(concessionsRes.data.filter(c => c.is_active));
      setSeasonalityData(seasonalityRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // Filter objectives by concession
  const filteredObjectives = selectedConcession === "all" 
    ? objectives 
    : objectives.filter(o => o.concession_id === selectedConcession);

  // Calculate monthly data for charts
  const getMonthlyChartData = () => {
    const data = [];
    
    for (let month = 1; month <= 12; month++) {
      const monthObjectives = filteredObjectives.filter(o => o.month === month);
      const signedObjectives = monthObjectives.filter(o => o.status === "signed");
      
      // Get seasonality budget for this month
      let budgetVN = 0, budgetVO = 0;
      const relevantConcessions = selectedConcession === "all" 
        ? concessions 
        : concessions.filter(c => c.id === selectedConcession);
      
      relevantConcessions.forEach(concession => {
        const seasonality = seasonalityData.find(
          s => s.concession_name === concession.name && s.month === month
        );
        if (seasonality) {
          budgetVN += Math.round((concession.budget_vn || 0) * (seasonality.vn_percent || 0) / 100);
          budgetVO += Math.round((concession.budget_vo || 0) * (seasonality.vo_percent || 0) / 100);
        }
      });
      
      data.push({
        month: monthNames[month - 1],
        monthFull: monthNamesFull[month - 1],
        objectifsVN: monthObjectives.reduce((sum, o) => sum + (o.vn_target || 0), 0),
        objectifsVO: monthObjectives.reduce((sum, o) => sum + (o.vo_target || 0), 0),
        signesVN: signedObjectives.reduce((sum, o) => sum + (o.vn_target || 0), 0),
        signesVO: signedObjectives.reduce((sum, o) => sum + (o.vo_target || 0), 0),
        budgetVN,
        budgetVO,
        financement: monthObjectives.reduce((sum, o) => sum + (o.financing_target || 0), 0),
        vendeurs: monthObjectives.length
      });
    }
    
    return data;
  };

  // Calculate quarterly data for Fleet charts
  const getQuarterlyChartData = () => {
    const data = [];
    
    for (let quarter = 1; quarter <= 4; quarter++) {
      const months = quarterMonths[quarter];
      const quarterObjectives = filteredObjectives.filter(o => months.includes(o.month));
      const signedObjectives = quarterObjectives.filter(o => o.status === "signed");
      
      data.push({
        quarter: quarterNames[quarter - 1],
        quarterFull: `Trimestre ${quarter}`,
        objectifsVN: quarterObjectives.reduce((sum, o) => sum + (o.vn_target || 0), 0),
        objectifsVO: quarterObjectives.reduce((sum, o) => sum + (o.vo_target || 0), 0),
        signesVN: signedObjectives.reduce((sum, o) => sum + (o.vn_target || 0), 0),
        signesVO: signedObjectives.reduce((sum, o) => sum + (o.vo_target || 0), 0),
        financement: quarterObjectives.reduce((sum, o) => sum + (o.financing_target || 0), 0),
        vendeurs: quarterObjectives.length
      });
    }
    
    return data;
  };

  // Calculate status distribution for pie chart
  const getStatusDistribution = () => {
    const statusCount = {
      signed: 0,
      pending_seller: 0,
      pending_manager: 0,
      draft: 0,
      not_defined: 0
    };
    
    filteredObjectives.forEach(o => {
      if (statusCount[o.status] !== undefined) {
        statusCount[o.status]++;
      }
    });
    
    return [
      { name: 'Signés', value: statusCount.signed, color: '#10B981' },
      { name: 'Attente vendeur', value: statusCount.pending_seller, color: '#F59E0B' },
      { name: 'Attente manager', value: statusCount.pending_manager, color: '#3B82F6' },
      { name: 'Brouillon', value: statusCount.draft, color: '#6B7280' }
    ].filter(s => s.value > 0);
  };

  // Calculate YTD totals
  const calculateYTD = () => {
    const currentMonth = new Date().getMonth() + 1;
    const ytdObjectives = filteredObjectives.filter(o => o.month <= currentMonth);
    const signedObjectives = ytdObjectives.filter(o => o.status === "signed");
    
    return {
      totalVN: ytdObjectives.reduce((sum, o) => sum + (o.vn_target || 0), 0),
      totalVO: ytdObjectives.reduce((sum, o) => sum + (o.vo_target || 0), 0),
      signedVN: signedObjectives.reduce((sum, o) => sum + (o.vn_target || 0), 0),
      signedVO: signedObjectives.reduce((sum, o) => sum + (o.vo_target || 0), 0),
      financement: ytdObjectives.reduce((sum, o) => sum + (o.financing_target || 0), 0),
      objectifsCount: ytdObjectives.length,
      signedCount: signedObjectives.length
    };
  };

  // Calculate performance vs budget
  const getPerformanceVsBudget = () => {
    const currentMonth = new Date().getMonth() + 1;
    let totalBudgetVN = 0, totalBudgetVO = 0;
    let totalObjectiveVN = 0, totalObjectiveVO = 0;
    
    const relevantConcessions = selectedConcession === "all" 
      ? concessions 
      : concessions.filter(c => c.id === selectedConcession);
    
    for (let month = 1; month <= currentMonth; month++) {
      const monthObjectives = filteredObjectives.filter(o => o.month === month);
      totalObjectiveVN += monthObjectives.reduce((sum, o) => sum + (o.vn_target || 0), 0);
      totalObjectiveVO += monthObjectives.reduce((sum, o) => sum + (o.vo_target || 0), 0);
      
      relevantConcessions.forEach(concession => {
        const seasonality = seasonalityData.find(
          s => s.concession_name === concession.name && s.month === month
        );
        if (seasonality) {
          totalBudgetVN += Math.round((concession.budget_vn || 0) * (seasonality.vn_percent || 0) / 100);
          totalBudgetVO += Math.round((concession.budget_vo || 0) * (seasonality.vo_percent || 0) / 100);
        }
      });
    }
    
    return {
      vn: {
        budget: totalBudgetVN,
        objective: totalObjectiveVN,
        percent: totalBudgetVN > 0 ? Math.round((totalObjectiveVN / totalBudgetVN) * 100) : 0
      },
      vo: {
        budget: totalBudgetVO,
        objective: totalObjectiveVO,
        percent: totalBudgetVO > 0 ? Math.round((totalObjectiveVO / totalBudgetVO) * 100) : 0
      }
    };
  };

  const monthlyData = getMonthlyChartData();
  const statusData = getStatusDistribution();
  const ytd = calculateYTD();
  const performance = getPerformanceVsBudget();

  const getTrendIcon = (percent) => {
    if (percent >= 100) return <ArrowUp className="w-4 h-4 text-green-500" />;
    if (percent >= 90) return <Minus className="w-4 h-4 text-amber-500" />;
    return <ArrowDown className="w-4 h-4 text-red-500" />;
  };

  if (loading) {
    return (
      <Layout title="Suivi de performance">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E30613]" />
        </div>
      </Layout>
    );
  }

  const isAdmin = user?.role === "admin" || user?.role === "hr";

  return (
    <Layout title="Suivi de performance">
      {/* Header with filters */}
      <div className="bg-gradient-to-br from-[#212E38] to-[#1a252e] rounded-2xl p-6 text-white mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/10 p-3 rounded-xl">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Suivi de Performance {selectedYear}
              </h2>
              <p className="text-gray-400 text-sm">Analyse des objectifs et tendances</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Select value={selectedConcession} onValueChange={setSelectedConcession}>
                <SelectTrigger className="w-52 bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Toutes les concessions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les concessions</SelectItem>
                  {concessions.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-24 bg-white/10 border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isFleetSelected ? '' : 'lg:grid-cols-4'} gap-4 mb-8`}>
        <div className="bg-white rounded-xl p-5 shadow-sm border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-500">Cumul VN (YTD)</span>
            {getTrendIcon(performance.vn.percent)}
          </div>
          <div className="text-3xl font-bold text-[#0F172A]">{ytd.totalVN}</div>
          <div className="text-sm text-gray-500 mt-1">
            vs budget: {performance.vn.budget} ({performance.vn.percent}%)
          </div>
        </div>
        
        {!isFleetSelected && (
          <>
            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-500">Cumul VO (YTD)</span>
                {getTrendIcon(performance.vo.percent)}
              </div>
              <div className="text-3xl font-bold text-[#0F172A]">{ytd.totalVO}</div>
              <div className="text-sm text-gray-500 mt-1">
                vs budget: {performance.vo.budget} ({performance.vo.percent}%)
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-500">Financement (YTD)</span>
                <Target className="w-4 h-4 text-[#E30613]" />
              </div>
              <div className="text-3xl font-bold text-[#0F172A]">
                {(ytd.financement / 1000).toFixed(0)}k€
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {ytd.objectifsCount} objectifs définis
              </div>
            </div>
          </>
        )}
        
        <div className="bg-white rounded-xl p-5 shadow-sm border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-500">Taux de signature</span>
            <BarChart3 className="w-4 h-4 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-[#0F172A]">
            {ytd.objectifsCount > 0 ? Math.round((ytd.signedCount / ytd.objectifsCount) * 100) : 0}%
          </div>
          <div className="text-sm text-gray-500 mt-1">
            {ytd.signedCount} / {ytd.objectifsCount} signés
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* VN/VO Evolution Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <h3 className="text-lg font-bold text-[#212E38] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {isFleetSelected ? (
              <>
                <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-sm mr-2">FLEET</span>
                Évolution trimestrielle VN
              </>
            ) : (
              'Évolution des objectifs VN/VO'
            )}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={isFleetSelected ? getQuarterlyChartData() : monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey={isFleetSelected ? "quarter" : "month"} tick={{ fill: '#666', fontSize: 12 }} />
              <YAxis tick={{ fill: '#666', fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                labelFormatter={(label) => isFleetSelected ? `Trimestre ${label}` : monthNamesFull[monthNames.indexOf(label)]}
              />
              <Legend />
              <Line type="monotone" dataKey="objectifsVN" name="Objectifs VN" stroke="#E30613" strokeWidth={2} dot={{ fill: '#E30613' }} />
              {!isFleetSelected && <Line type="monotone" dataKey="objectifsVO" name="Objectifs VO" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6' }} />}
              {!isFleetSelected && <Line type="monotone" dataKey="budgetVN" name="Budget VN" stroke="#E30613" strokeWidth={1} strokeDasharray="5 5" dot={false} />}
              {!isFleetSelected && <Line type="monotone" dataKey="budgetVO" name="Budget VO" stroke="#3B82F6" strokeWidth={1} strokeDasharray="5 5" dot={false} />}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Status Distribution Pie Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <h3 className="text-lg font-bold text-[#212E38] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Répartition des statuts
          </h3>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-400">
              Aucune donnée disponible
            </div>
          )}
        </div>
      </div>

      {/* Bar Chart - Comparison */}
      <div className="bg-white rounded-xl p-6 shadow-sm border mb-8">
        <h3 className="text-lg font-bold text-[#212E38] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {isFleetSelected ? (
            <>
              <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-sm mr-2">FLEET</span>
              Objectifs par trimestre
            </>
          ) : (
            'Comparaison mensuelle : Objectifs vs Budget'
          )}
        </h3>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={isFleetSelected ? getQuarterlyChartData() : monthlyData} barGap={0}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey={isFleetSelected ? "quarter" : "month"} stroke="#888" fontSize={12} />
            <YAxis stroke="#888" fontSize={12} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
              labelFormatter={(label) => isFleetSelected ? `Trimestre ${label}` : monthNamesFull[monthNames.indexOf(label)]}
            />
            <Legend />
            <Bar dataKey="objectifsVN" name="Objectifs VN" fill="#E30613" radius={[4, 4, 0, 0]} />
            {!isFleetSelected && <Bar dataKey="budgetVN" name="Budget VN" fill="#FCA5A5" radius={[4, 4, 0, 0]} />}
            <Bar dataKey="objectifsVO" name="Objectifs VO" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            {!isFleetSelected && <Bar dataKey="budgetVO" name="Budget VO" fill="#94A3B8" radius={[4, 4, 0, 0]} />}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Details Table */}
      <div className="bg-white rounded-xl p-6 shadow-sm border">
        <h3 className="text-lg font-bold text-[#212E38] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {isFleetSelected ? 'Détail trimestriel Fleet' : 'Détail mensuel'}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left py-3 px-4 font-semibold">{isFleetSelected ? 'Trimestre' : 'Mois'}</th>
                <th className="text-right py-3 px-4 font-semibold">Obj. VN</th>
                {!isFleetSelected && <th className="text-right py-3 px-4 font-semibold">Budget VN</th>}
                {!isFleetSelected && <th className="text-right py-3 px-4 font-semibold">Écart VN</th>}
                <th className="text-right py-3 px-4 font-semibold">Obj. VO</th>
                {!isFleetSelected && <th className="text-right py-3 px-4 font-semibold">Budget VO</th>}
                {!isFleetSelected && <th className="text-right py-3 px-4 font-semibold">Écart VO</th>}
                <th className="text-right py-3 px-4 font-semibold">Financement</th>
                {isFleetSelected && <th className="text-right py-3 px-4 font-semibold">Vendeurs</th>}
              </tr>
            </thead>
            <tbody>
              {(isFleetSelected ? getQuarterlyChartData() : monthlyData).map((row, idx) => {
                const ecartVN = row.objectifsVN - (row.budgetVN || 0);
                const ecartVO = row.objectifsVO - (row.budgetVO || 0);
                return (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">
                      {isFleetSelected ? (
                        <span className="flex items-center gap-2">
                          <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-semibold">{row.quarter}</span>
                          {row.quarterFull}
                        </span>
                      ) : row.monthFull}
                    </td>
                    <td className="text-right py-3 px-4 font-medium">{row.objectifsVN}</td>
                    {!isFleetSelected && <td className="text-right py-3 px-4 text-gray-500">{row.budgetVN}</td>}
                    {!isFleetSelected && (
                      <td className={`text-right py-3 px-4 font-medium ${ecartVN >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {ecartVN >= 0 ? '+' : ''}{ecartVN}
                      </td>
                    )}
                    <td className="text-right py-3 px-4 font-medium">{row.objectifsVO}</td>
                    {!isFleetSelected && <td className="text-right py-3 px-4 text-gray-500">{row.budgetVO}</td>}
                    {!isFleetSelected && (
                      <td className={`text-right py-3 px-4 font-medium ${ecartVO >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {ecartVO >= 0 ? '+' : ''}{ecartVO}
                      </td>
                    )}
                    <td className="text-right py-3 px-4">{row.financement.toLocaleString()}€</td>
                    {isFleetSelected && <td className="text-right py-3 px-4 text-gray-500">{row.vendeurs}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
